import { useErrorHandler } from "@@common";
import { IExpression, IExpressionEvaluatorContext, IVariableScope, ObjectVariableScope, VariableScopeObserver } from "@@expressions";
import { useCallback, useEffect, useState } from "react";
import { useObservedExpressionResult } from "./useObservedExpressionResult";

const NULL_RESULT: useObservedExpressionResult<any> = [undefined, false, () => Promise.resolve(undefined)];

export default function useObservedExpression<T = any>(
  expression: IExpression | undefined,
  context: IExpressionEvaluatorContext,
  loggerName: string,
  valueTransformer?: (value: any, error: any) => T
): useObservedExpressionResult<T> {
  if (!expression) {
    return NULL_RESULT;
  }

  // Note: These hooks are placed in this condition on purpoise to avoid unnecessary evaluation
  // The component definition WILL NOT change during the lifecycle of the component, so it's safe to call hooks conditionally based on its properties

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [value, setValue] = useState<T | undefined>();
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [isPending, setIsPending] = useState(false);
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const errorHandler = useErrorHandler();

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const evaluateValue = useCallback(
    async (silentRefresh?: boolean) => {
      if (!expression) {
        return;
      }

      if (!silentRefresh) {
        setIsPending(true);
      }
      try {
        const evaluatedValue = await context.Evaluator.evaluate(expression, context);

        const transformedValue = valueTransformer ? valueTransformer(evaluatedValue, null) : evaluatedValue;
        setValue(transformedValue);

        return transformedValue;
      } catch (error) {
        errorHandler(error);

        console.error(`[${loggerName}] Error evaluating expression:`, error);

        const transformedValue = valueTransformer ? valueTransformer(undefined, error) : undefined;
        setValue(transformedValue);

        return transformedValue;
      } finally {
        if (!silentRefresh) {
          setIsPending(false);
        }
      }
      // exclude valueTransformer at it never will be memorized
    },
    [expression, context, setValue]
  );

  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    let observedPaths: string[] = [];

    if (expression) {
      const observedVariables = context.Evaluator.collectReferencedVariables(expression);
      observedPaths = observedVariables.map((varExpression) => varExpression.Value.join("."));
    }

    evaluateValue();

    if (observedPaths.length === 0) {
      // No dynamic paths to observe, nothing to do
      //console.log(`[${loggerName}] No observed paths for expression`);
      return;
    }

    //console.log(`[${loggerName}] Observed paths for expression:`, observedPaths);

    const handleVariableScopeChanged: VariableScopeObserver = (target: IVariableScope, name: string | null, value: any, prevValue: any) => {
      //console.log(`[${loggerName}] Variable scope changed for ${name}`, observedPaths);
      if (name !== null && !observedPaths.some((observedPath) => observedPath.startsWith(name))) {
        return;
      }

      console.log(`[${loggerName}] Variable scope changed for ${name}, updating value`);
      evaluateValue(true);
    };

    // Register observer to update text on variable scope changes
    const objectVariableScope = context.VariableScope as ObjectVariableScope;
    console.log(`[${loggerName}] Adding observer to variable scope.`);
    objectVariableScope.addObserver(handleVariableScopeChanged);

    return () => {
      console.log(`[${loggerName}] Removing observer from variable scope.`);
      objectVariableScope.removeObserver(handleVariableScopeChanged);
    };
  }, [context.VariableScope, expression]);

  return [value, isPending, evaluateValue];
}
